import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setViewMode, setSortBy, setSortDirection } from '../../../../store/reducers/dropbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faThLarge, 
  faList, 
  faUpload, 
  faCog,
  faChevronDown,
  faSearch,
  faFolderOpen,
  faSortAmountDown,
  faSortAmountUp
} from '@fortawesome/free-solid-svg-icons';
import GlobalSearch from '../Search/GlobalSearch';

interface DropboxHeaderNewProps {
  currentFolderName: string;
  onNewFolder: () => void;
  onUpload: () => void;
  onViewChange: (view: 'all' | 'favorites' | 'recent' | 'trash') => void;
  currentView: 'all' | 'favorites' | 'recent' | 'trash';
  folders: any[];
  currentFolderId: string | null;
  onFolderClick: (folderId: string | null) => void;
}

const DropboxHeaderNew: React.FC<DropboxHeaderNewProps> = ({ 
  currentFolderName, 
  onNewFolder, 
  onUpload,
  onViewChange,
  currentView,
  folders,
  currentFolderId,
  onFolderClick
}) => {
  const dispatch = useDispatch();
  const { viewMode, sortBy, sortDirection } = useSelector((state: any) => state.dropbox);
  
  // Role-based permissions
  const canAccess = useSelector((state: any) => state.auth.permissions?.opsmanual);

  const toggleSortDirection = () => {
    dispatch(setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'));
  };

  // Build folder path from folder hierarchy with IDs
  const buildFolderPath = (folderId: string | null): Array<{ id: string | null; name: string }> => {
    if (!folderId) return [{ id: null, name: 'All files' }];
    
    const path: Array<{ id: string | null; name: string; parentId: string | null }> = [];
    let currentId = folderId;
    
    while (currentId) {
      const folder = folders.find((f: any) => f.id === currentId);
      if (folder) {
        path.unshift({ id: folder.id, name: folder.name, parentId: folder.parent_id });
        currentId = folder.parent_id;
      } else {
        break;
      }
    }
    
    return [{ id: null, name: 'All files' }, ...path];
  };

  const breadcrumbPath = buildFolderPath(currentFolderId);

  const handleBreadcrumbClick = (folderId: string | null) => {
    onFolderClick(folderId);
  };

  return (
    <div className="dropbox-header-new">
      {/* First Row: Breadcrumb + Folder Name | Upload + New Folder */}
      <div className="dropbox-header-middle-row">
        <div className="breadcrumb-folder-section">
          <div className="folder-name-large">
            <FontAwesomeIcon icon={faFolderOpen} className="folder-icon-header" />
            <h1>{currentFolderName}</h1>
          </div>
          <div className="breadcrumb-path">
            {breadcrumbPath.map((item, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span className="breadcrumb-sep">/</span>}
                <span 
                  className={`breadcrumb-item-new ${index < breadcrumbPath.length - 1 ? 'clickable' : 'current'}`}
                  onClick={() => index < breadcrumbPath.length - 1 ? handleBreadcrumbClick(item.id) : null}
                  style={{ 
                    cursor: index < breadcrumbPath.length - 1 ? 'pointer' : 'default',
                    color: index < breadcrumbPath.length - 1 ? '#007bff' : '#333'
                  }}
                  title={index < breadcrumbPath.length - 1 ? `Navigate to ${item.name}` : ''}
                >
                  {item.name}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="header-actions-right">
          {canAccess?.['upload-document'] && (
            <button className="btn-upload-header" onClick={onUpload}>
              <FontAwesomeIcon icon={faUpload} className="mr-2" />
              <span className="btn-text">Upload</span>
              <FontAwesomeIcon icon={faChevronDown} className="ml-2" style={{ fontSize: '0.75rem' }} />
            </button>
          )}
          {canAccess?.['create-new-folder'] && (
            <button className="btn-new-folder-header" onClick={onNewFolder}>
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              <span className="btn-text">New folder</span>
            </button>
          )}
        </div>
      </div>

      {/* Second Row: Search + View Controls */}
      <div className="dropbox-header-top-row">
        <div className="header-search-full">
          <GlobalSearch />
        </div>

        <div className="view-controls-top">
          <div className="btn-group-header">
            <button
              className={`view-btn-header ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => dispatch(setViewMode('grid'))}
              title="Grid view"
            >
              <FontAwesomeIcon icon={faThLarge} />
            </button>
            <button
              className={`view-btn-header ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => dispatch(setViewMode('list'))}
              title="List view"
            >
              <FontAwesomeIcon icon={faList} />
            </button>
          </div>

          <select
            className="sort-select-header"
            value={sortBy}
            onChange={(e) => {
              dispatch(setSortBy(e.target.value as any));
            }}
            disabled={currentView === 'recent'}
            style={{ opacity: currentView === 'recent' ? 0.5 : 1, cursor: currentView === 'recent' ? 'not-allowed' : 'pointer' }}
          >
            <option value="name">Name</option>
            <option value="date">Date Modified</option>
            <option value="size">Size</option>
            <option value="custom">Order</option>
          </select>

          <button
            className="view-btn-header sort-direction"
            onClick={toggleSortDirection}
            title={sortDirection === 'asc' ? 'Sort Ascending' : 'Sort Descending'}
            disabled={currentView === 'recent'}
            style={{ opacity: currentView === 'recent' ? 0.5 : 1, cursor: currentView === 'recent' ? 'not-allowed' : 'pointer' }}
          >
            <FontAwesomeIcon icon={sortDirection === 'asc' ? faSortAmountDown : faSortAmountUp} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DropboxHeaderNew;
